import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@jobenvoy/ui";
import { getReady } from "@/lib/api/envoy";
import { keys } from "@/lib/hooks/keys";
import { playReadySound, unlockReadySound } from "@/lib/readySound";
import { postingDetailPath, ROUTES } from "@/lib/routes";

const UNLOCK_EVENTS = ["pointerup", "touchend", "click", "keydown"] as const;

/** One monitor across all signed-in pages. The initial backlog stays quiet. */
export default function ReadyNotifications({ userId }: { userId: string }) {
    const toast = useToast();
    const navigate = useNavigate();
    const client = useQueryClient();
    const seen = useRef<Set<string> | null>(null);
    const ready = useQuery({
        queryKey: ["envoy", "ready-notifications", userId],
        queryFn: async () => {
            const applications = [];
            let cursor: string | undefined;
            do {
                const page = await getReady(200, cursor);
                applications.push(...page.applications);
                cursor = page.next_cursor ?? undefined;
            } while (cursor);
            return applications;
        },
        refetchInterval: 15_000,
    });
    // The events a phone counts as a gesture that may start audio: a touch
    // lifting, a click, a key. A touch going down is not one, and a context
    // made from it starts suspended.
    useEffect(() => {
        for (const type of UNLOCK_EVENTS) document.addEventListener(type, unlockReadySound);
        return () => {
            for (const type of UNLOCK_EVENTS) document.removeEventListener(type, unlockReadySound);
        };
    }, []);
    useEffect(() => {
        if (!ready.data) return;
        const ids = ready.data.map(({ application }) => application.application_id);
        if (seen.current === null) {
            seen.current = new Set(ids);
            return;
        }
        const fresh = ready.data.filter(
            ({ application }) => !seen.current!.has(application.application_id),
        );
        ids.forEach((id) => seen.current!.add(id));
        if (fresh.length === 0) return;
        const first = fresh[0].application;
        toast.show({
            variant: "success",
            title:
                fresh.length === 1
                    ? "Your application is ready"
                    : `${fresh.length} applications are ready`,
            description:
                fresh.length === 1
                    ? `${first.title} at ${first.company}. Your tailored resume is ready to review.`
                    : "Your envoy has finished preparing them. Take a look when you are ready.",
            duration: 12_000,
            action: {
                label: fresh.length === 1 ? "Review application" : "View ready applications",
                onClick: () =>
                    navigate(
                        fresh.length === 1
                            ? postingDetailPath(first.posting_id)
                            : ROUTES.APPLICATIONS,
                    ),
            },
        });
        playReadySound();
        void client.invalidateQueries({
            queryKey: keys.envoy.all,
            predicate: (query) => query.queryKey[1] !== "ready-notifications",
        });
        // A finished tailor is a spent one: the tailors pill is asked again.
        void client.invalidateQueries({ queryKey: keys.billing.all });
    }, [ready.data, toast, navigate, client]);
    return null;
}
